import {
  type DealQueueRow,
  type DealQueueSort,
  type DealQueueTab,
} from '@carsfinders/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { cn } from '../../bids/utils';
import {
  FINANCE_CLIENTS_QUERY_KEY,
  PRICING_RULES_QUERY_KEY,
  fetchFinanceClients,
  fetchPricingRules,
} from '../../finance/api';
import { IssueInvoiceDrawer } from '../../finance/components/issue-invoice-drawer';
import { DEALS_QUEUE_QUERY_KEY } from '../api';
import { useDealsQueue } from '../hooks/use-deals-queue';
import { DEAL_ACTION_CELL, DEAL_GRID, DealRow } from './deal-row';
import { DealsTabs, dealsTabId } from './deals-tabs';

const COLUMNS = [
  { key: 'lot', align: '' },
  { key: 'client', align: '' },
  { key: 'bid', align: 'text-right' },
  { key: 'stage', align: '' },
  { key: 'action', align: 'text-right' },
] as const;

interface InvoiceTarget {
  userId: string;
  dealId: string;
  /** The deal row's client label — shown when the finance roll-up has no row for them. */
  clientName: string;
}

export interface DealsBoardProps {
  tab: DealQueueTab;
  sort: DealQueueSort;
  onTabChange: (tab: DealQueueTab) => void;
  onSortChange: (sort: DealQueueSort) => void;
  /** Debounced, trimmed search; '' = none. */
  q?: string;
  /** One client's lots only — the client page's «Ставки» tab, or «Угоди»'s «Клієнт» filter. */
  userId?: string;
  /** Show the «Клієнт» filter («Угоди» only); called with '' for «Усі клієнти». */
  onClientChange?: (clientId: string) => void;
  /** One auction house's lots only — «Угоди»'s «Аукціон» filter. */
  auction?: string;
  /** Show the «Аукціон» filter; called with '' for «Усі аукціони». */
  onAuctionChange?: (auction: string) => void;
}

/**
 * The «Угоди» board — tabs, «Етап» and sort filters, the rows with their next
 * action, «Показати ще» and the invoice drawer. ONE component for two places
 * (owner, 2026-10-12: the client page's «Ставки» shows its lots «так само як
 * на сторінці угод»): the «Угоди» page, and the client page scoped by
 * `userId`. The queue cache is shared by key, so the page's own counters read
 * the same request this board makes.
 */
export function DealsBoard({
  tab,
  sort,
  onTabChange,
  onSortChange,
  q = '',
  userId,
  onClientChange,
  auction,
  onAuctionChange,
}: DealsBoardProps) {
  const queryClient = useQueryClient();
  const queue = useDealsQueue(tab, q, sort, userId, auction);

  const [invoiceTarget, setInvoiceTarget] = useState<InvoiceTarget | null>(null);
  const panelId = useId();
  const lastTrigger = useRef<HTMLElement | null>(null);

  const drawerOpen = invoiceTarget !== null;
  const clientsQuery = useQuery({
    queryKey: FINANCE_CLIENTS_QUERY_KEY,
    queryFn: fetchFinanceClients,
    enabled: drawerOpen,
  });
  const rulesQuery = useQuery({
    queryKey: PRICING_RULES_QUERY_KEY,
    queryFn: fetchPricingRules,
    enabled: drawerOpen,
  });

  const drawerFeedFailed = drawerOpen && (clientsQuery.isError || rulesQuery.isError);
  useEffect(() => {
    if (!drawerFeedFailed) return;
    // The drawer has nothing to quote without both; never mount it half-fed.
    setInvoiceTarget(null);
    toast.error(t('admin.deals.notice.failed'));
  }, [drawerFeedFailed]);

  const openInvoice = useCallback((row: DealQueueRow, trigger: HTMLElement) => {
    if (row.dealId === null) {
      // A won bid with no deal yet (legacy win) cannot be billed: the car
      // invoice must name a deal. Say so instead of opening a drawer that 400s.
      toast.error(t('admin.deals.notice.failed'));
      return;
    }
    lastTrigger.current = trigger;
    setInvoiceTarget({ userId: row.clientId, dealId: row.dealId, clientName: row.clientName });
  }, []);

  const closeInvoice = useCallback(() => {
    setInvoiceTarget(null);
    // An issued invoice moves the row to «Оплата»; a refused one changes nothing,
    // and a re-read is cheap either way.
    void queryClient.invalidateQueries({ queryKey: DEALS_QUEUE_QUERY_KEY });
    const trigger = lastTrigger.current;
    // Back to the row's button — or nowhere useful if the row moved on.
    if (trigger && trigger.isConnected) trigger.focus();
  }, [queryClient]);

  const counts = queue.counts;
  const rows = queue.rows;
  const drawerReady = drawerOpen && clientsQuery.data && rulesQuery.data;
  const selectTab = onTabChange;
  const selectSort = onSortChange;

  return (
    <>
      <DealsTabs
        active={tab}
        counts={counts}
        onChange={selectTab}
        panelId={panelId}
        sort={sort}
        onSortChange={selectSort}
        clientFilter={
          onClientChange
            ? { clients: queue.clients, clientId: userId ?? '', onChange: onClientChange }
            : undefined
        }
        auctionFilter={
          onAuctionChange
            ? { auctions: queue.auctions, auction: auction ?? '', onChange: onAuctionChange }
            : undefined
        }
      />

      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={dealsTabId(tab)}
        aria-busy={queue.isFetching}
      >
        {queue.isLoading ? (
          <p className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center text-[13px] text-cf-ink-subtle">
            {t('admin.deals.loading')}
          </p>
        ) : queue.isError ? (
          <section
            role="alert"
            className="rounded-cf-lg border border-cf-line bg-cf-surface p-10 text-center"
          >
            <p className="text-lg font-semibold">{t('admin.deals.loadError')}</p>
            <button
              type="button"
              onClick={() => void queue.refetch()}
              className="mt-4 h-10 rounded-cf-md bg-cf-brand px-4 text-sm font-semibold text-white hover:bg-cf-brand-hover"
            >
              {t('admin.deals.retry')}
            </button>
          </section>
        ) : rows.length === 0 ? (
          <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-6 py-10 text-center">
            <img
              src="/assets/cf/illu/bid.png"
              alt=""
              className="mx-auto mb-3 block h-[100px] w-auto"
            />
            <p className="text-lg font-semibold">
              {q ? t('admin.deals.emptySearch', { q }) : t('admin.deals.empty')}
            </p>
          </section>
        ) : (
          <section
            role="table"
            aria-label={t('admin.deals.title')}
            className="overflow-x-auto rounded-cf-lg border border-cf-line bg-cf-surface"
          >
            <div
              role="row"
              className={cn(
                DEAL_GRID,
                'border-b border-cf-line-soft px-[18px] py-2.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase',
              )}
            >
              {COLUMNS.map((column) => (
                <span
                  key={column.key}
                  role="columnheader"
                  className={cn(
                    column.align,
                    // BUG-18: the header sticks with its column — labelled when scrolled.
                    column.key === 'action' && cn(DEAL_ACTION_CELL, 'flex flex-col justify-center'),
                  )}
                >
                  {t(`admin.deals.columns.${column.key}`)}
                </span>
              ))}
            </div>
            {rows.map((row) => (
              <DealRow key={row.bidId} row={row} onIssueInvoice={openInvoice} />
            ))}
          </section>
        )}

        {queue.hasNextPage && !queue.isError && (
          <div className="mt-3 flex justify-center">
            <button
              type="button"
              onClick={() => void queue.fetchNextPage()}
              disabled={queue.isFetchingNextPage}
              className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-4 text-sm font-semibold text-cf-ink hover:border-cf-ink disabled:opacity-50"
            >
              {queue.isFetchingNextPage ? t('admin.deals.loadingMore') : t('admin.deals.loadMore')}
            </button>
          </div>
        )}
      </div>

      {drawerReady && invoiceTarget && (
        <IssueInvoiceDrawer
          clients={clientsQuery.data}
          rules={rulesQuery.data}
          prefill={{
            userId: invoiceTarget.userId,
            dealId: invoiceTarget.dealId,
            clientName: invoiceTarget.clientName,
          }}
          onClose={closeInvoice}
        />
      )}
    </>
  );
}
